import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { Label } from "./ui/label";
import api from "../lib/api";
import { brand } from "../brand";
import { gymLoginUrl } from "../lib/gymAddress";

const DEBOUNCE_MS = 400;

// The gym's web address on the signup form: suggested from the gym name until the owner types their own, and checked
// with the API as they type. Reports the address through onChange once it is free, and "" while it is not.
export function GymAddressField({ suggestFrom, onChange }) {
  const [address, setAddress] = useState("");
  const [edited, setEdited] = useState(false);
  const [status, setStatus] = useState({ state: "idle" });

  useEffect(() => {
    if (edited || !suggestFrom.trim()) return undefined;
    let cancelled = false;
    setStatus({ state: "checking" });
    const timer = setTimeout(() => {
      api.get("/auth/gym-address", { params: { name: suggestFrom } })
        .then(({ data }) => { if (!cancelled) { setAddress(data.address); setStatus({ state: "ok" }); } })
        .catch(() => { if (!cancelled) setStatus({ state: "problem", message: "Could not suggest an address. Please type one." }); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [suggestFrom, edited]);

  useEffect(() => {
    if (!edited) return undefined;
    if (!address) { setStatus({ state: "idle" }); return undefined; }
    let cancelled = false;
    setStatus({ state: "checking" });
    const timer = setTimeout(() => {
      api.get("/auth/gym-address", { params: { address } })
        .then(({ data }) => {
          if (!cancelled) setStatus(data.available ? { state: "ok" } : { state: "problem", message: data.problem });
        })
        .catch(() => { if (!cancelled) setStatus({ state: "problem", message: "Could not check the address. Please try again." }); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [address, edited]);

  useEffect(() => {
    onChange(status.state === "ok" ? address : "");
  }, [address, status.state, onChange]);

  const type = (e) => {
    setEdited(true);
    setAddress(e.target.value.toLowerCase().replace(/\s+/g, "-"));
  };
  const shown = address ? gymLoginUrl(address).replace(/^https?:\/\//, "").replace(/\/$/, "") : "";

  return (
    <div>
      <Label htmlFor="gym-address" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
        Gym web address <span className="text-destructive">*</span>
      </Label>
      <div className="mt-1 flex h-11 items-center rounded-sm bg-secondary focus-within:ring-2 focus-within:ring-primary/30">
        <input
          id="gym-address"
          value={address}
          onChange={type}
          placeholder="your-gym"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={30}
          aria-describedby="gym-address-status"
          className="h-full min-w-0 flex-1 bg-transparent px-3 text-base outline-none focus:ring-0 md:text-sm"
          data-testid="signup-gym-address-input"
        />
        {brand.tenantDomain && (
          <span className="shrink-0 pr-3 text-sm text-muted-foreground">.{brand.tenantDomain}</span>
        )}
      </div>
      <p id="gym-address-status" aria-live="polite" className="mt-1 min-h-[1rem] text-xs">
        {status.state === "checking" && <span className="text-muted-foreground">Checking…</span>}
        {status.state === "ok" && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Check className="h-3 w-3 text-green-500" /> Your staff and members sign in at {shown}
          </span>
        )}
        {status.state === "problem" && (
          <span className="flex items-center gap-1 text-destructive">
            <X className="h-3 w-3" /> {status.message}
          </span>
        )}
      </p>
    </div>
  );
}
